import type { MemoryFact, MemoryListResponse } from "@ark-bot/protocol";
import { useCallback, useEffect, useState } from "react";

import { api } from "../api.js";
import { useText } from "../i18n.js";
import { MEMORY } from "../text/memory.js";

import { useConfirm } from "./ConfirmDialog.js";

interface Props {
  readonly agentId: string;
  readonly agentName: string;
  /** Bumped by a `memory.changed` event, so a fact the agent just learned appears here. */
  readonly revision: number;
  readonly onClose: () => void;
}

const day = (ms: number): string => (Number.isFinite(ms) && ms > 0 ? new Date(ms).toISOString().slice(0, 10) : "");

/** Why the bot believes a fact, or why it let one go: who changed it, the message it came from, and what it replaced. Nothing for facts older than the history. */
function Provenance({ fact, forgotten }: { readonly fact: MemoryFact; readonly forgotten?: boolean }): React.JSX.Element | null {
  const t = useText(MEMORY);
  const lines: string[] = [];
  if (fact.source != null) {
    const how = (forgotten === true ? t.dropped : t.learned)(fact.source.by, day(fact.source.at));
    lines.push(fact.source.said == null ? how : `${how}, ${t.fromMessage(fact.source.said)}`);
  }
  if (fact.replaced != null && fact.replaced.length > 0) lines.push(t.replaced(fact.replaced.map((text) => `“${text}”`).join("; ")));
  if (fact.replacedBy != null) lines.push(t.replacedBy(`“${fact.replacedBy}”`));
  if (lines.length === 0) return null;
  return (
    <>
      {lines.map((line) => (
        <span key={line} className="memory-source muted">
          {line}
        </span>
      ))}
    </>
  );
}

/** One fact as a row: the day it was learned, the words and where they came from, and Forget when it is this agent's to forget. */
function FactRow({ fact, busy, onForget }: { readonly fact: MemoryFact; readonly busy: boolean; readonly onForget: (fact: MemoryFact) => void }): React.JSX.Element {
  const t = useText(MEMORY);
  return (
    <li className="memory-fact">
      <span className="memory-date muted">{day(fact.createdAt)}</span>
      <span className="memory-text">
        {fact.content}
        <Provenance fact={fact} />
      </span>
      {fact.own ? (
        <button type="button" className="linkish memory-forget" disabled={busy} onClick={() => onForget(fact)} title={t.forgetHint}>
          {t.forget}
        </button>
      ) : (
        <span className="muted memory-via">{fact.via}</span>
      )}
    </li>
  );
}

function Group({ title, facts, busy, onForget, empty }: { readonly title: string; readonly facts: readonly MemoryFact[]; readonly busy: boolean; readonly onForget: (fact: MemoryFact) => void; readonly empty: string }): React.JSX.Element {
  return (
    <section className="details-section">
      <div className="details-section-head">
        <h3>{title}</h3>
        <span className="muted">{facts.length}</span>
      </div>
      {facts.length === 0 ? (
        <p className="muted">{empty}</p>
      ) : (
        <ul className="summary-list memory-list">
          {facts.map((fact) => (
            <FactRow key={`${fact.scope}-${fact.via}-${fact.id}`} fact={fact} busy={busy} onForget={onForget} />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * What the bot believes (GAPS §3, "a memory browser"): its profile of the
 * user, its dated log, what other bots have shared about the user, and what
 * it was told to forget. A fact forgotten here stays forgotten: the bot will
 * not re-learn it from a conversation unless someone says it again on
 * purpose. Clear all empties this bot's own memory and what it shared.
 */
export function Memory({ agentId, agentName, revision, onClose }: Props): React.JSX.Element {
  const t = useText(MEMORY);
  const [list, setList] = useState<MemoryListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showForgotten, setShowForgotten] = useState(false);

  const load = useCallback(async () => {
    try {
      setList(await api.memory(agentId));
      setError(null);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    }
  }, [agentId]);

  useEffect(() => {
    void load();
  }, [load, revision]);

  const confirm = useConfirm();
  const act = async (work: () => Promise<unknown>): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  };

  const forget = (fact: MemoryFact): void => void act(() => api.forgetMemory(agentId, fact.id));
  const restore = (fact: MemoryFact): void => void act(() => api.restoreMemory(agentId, fact.id));
  // The last Tidy now's outcome, worded at render so it follows the app's language.
  const [tidyResult, setTidyResult] = useState<{ readonly tidied: boolean; readonly summary?: string | undefined } | null>(null);
  const tidy = (): void =>
    void act(async () => {
      const result = await api.tidyMemory(agentId);
      setTidyResult({ tidied: result.tidied, summary: result.summary ?? undefined });
    });
  const undo = (): void => void act(() => api.undoMemoryTidy(agentId).then(() => setTidyResult(null)));
  const clearAll = async (): Promise<void> => {
    if (await confirm({ title: t.clearTitle(agentName), message: t.clearMessage, action: t.clearAction, danger: true })) void act(() => api.clearMemory(agentId));
  };
  const tidyNote = tidyResult == null ? null : tidyResult.tidied ? (tidyResult.summary ?? t.tidied) : t.nothingToTidy;

  const facts = list?.facts ?? [];
  const profile = facts.filter((fact) => fact.scope === "agent" && fact.kind === "profile");
  const log = facts.filter((fact) => fact.scope === "agent" && fact.kind === "log");
  const shared = facts.filter((fact) => fact.scope === "user");
  const forgotten = list?.forgotten ?? [];

  return (
    <div className="details-body memory-panel">
      <p className="muted">{t.intro(agentName)}</p>
      {error != null ? <div className="settings-error">{error}</div> : null}
      {list == null ? (
        <div className="muted">{t.loading}</div>
      ) : (
        <>
          <Group title={t.aboutYou} facts={profile} busy={busy} onForget={forget} empty={t.aboutYouEmpty} />
          <Group title={t.log} facts={log} busy={busy} onForget={forget} empty={t.logEmpty} />
          <Group title={t.shared} facts={shared} busy={busy} onForget={forget} empty={t.sharedEmpty} />
          <section className="details-section">
            <div className="details-section-head">
              <h3>{t.forgotten}</h3>
              <button type="button" className="linkish" onClick={() => setShowForgotten((open) => !open)}>
                {showForgotten ? t.hide : t.show(forgotten.length)}
              </button>
            </div>
            {showForgotten ? (
              forgotten.length === 0 ? (
                <p className="muted">{t.forgottenEmpty}</p>
              ) : (
                <ul className="summary-list memory-list">
                  {forgotten.map((fact) => (
                    <li key={fact.id} className="memory-fact">
                      <span className="memory-date muted">{day(fact.createdAt)}</span>
                      <span className="memory-text muted">
                        {fact.content}
                        <Provenance fact={fact} forgotten />
                      </span>
                      <button type="button" className="linkish memory-forget" disabled={busy} onClick={() => restore(fact)} title={t.restoreHint}>
                        {t.restore}
                      </button>
                    </li>
                  ))}
                </ul>
              )
            ) : null}
          </section>
          <section className="details-section">
            <div className="details-section-head">
              <h3>{t.archive}</h3>
              <span className="muted">{list.archive?.conversations ?? 0}</span>
            </div>
            <p className="muted">{t.archiveAbout(list.archive?.conversations ?? 0, day(list.archive?.since ?? 0))}</p>
          </section>
          <section className="details-section">
            <div className="details-section-head">
              <h3>{t.tidying}</h3>
              <button type="button" className="linkish" disabled={busy} onClick={tidy} title={t.tidyHint}>
                {t.tidyNow}
              </button>
            </div>
            <p className="muted">{t.tidyAbout}</p>
            {tidyNote != null ? <p className="muted">{tidyNote}</p> : null}
            {list.lastTidy != null ? (
              <div className="memory-tidy">
                <span className="muted">{t.lastTidy(day(list.lastTidy.at), list.lastTidy.summary)}</span>
                <button type="button" className="linkish" disabled={busy} onClick={undo}>
                  {t.undo}
                </button>
              </div>
            ) : null}
          </section>
          <div className="memory-actions">
            <button type="button" className="danger" disabled={busy || (profile.length === 0 && log.length === 0 && shared.every((fact) => !fact.own) && (list.archive?.conversations ?? 0) === 0)} onClick={() => void clearAll()}>
              {t.clearAll}
            </button>
            <button type="button" className="linkish" onClick={onClose}>
              {t.done}
            </button>
          </div>
          <p className="muted memory-directory">{list.directory}</p>
        </>
      )}
    </div>
  );
}
